import { useCallback, useEffect, useMemo, useState } from 'react';
import { Spinner } from '@chakra-ui/react';
import AutoFixHigh from '@mui/icons-material/AutoFixHigh';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useParams } from 'react-router-dom';
import Menu from '@/components/Menu';
import { useToastContext } from '@/contexts/toastContext';
import useApiFetch from '@/hooks/useApiFetch';
import useApiSubmit from '@/hooks/useApiSubmit';
import { useAutofillFromResume } from '@/hooks/useAutofillFromResume';
import candidateProfile, { NewProjectType } from '@/services/candidateProfile';
import candidatesServices from '@/services/candidates';
import TransparentButton from '@/trio-ui/TransparentButton';
import { TechStackType, FetchApiIds } from '@/types';
import CandidateProjectsComponent from './CandidateProjects.component';
import DialogBox from '../DialogBox';
import SectionBox from '../SectionBox';

const projectFields = {
  title: '',
  link: '',
  year: 'Select a year',
  skills: [],
  description: '',
};

type ProjectFieldsType = {
  id?: string;
  title: string;
  link: string;
  year: string;
  skills: string[];
  description: string;
  isEditing: boolean;
};

const defaultCandidateProject = {
  ...projectFields,
  isEditing: true,
};

export type CandidateProjectType = ProjectFieldsType;

export default function CandidateProjects(): JSX.Element {
  const toast = useToastContext();
  const { id } = useParams();
  const candidateId = id ?? '';

  const [projects, setProjects] = useState<CandidateProjectType[]>([]);
  const [projectIndexToDelete, setProjectIndexToDelete] = useState('');

  const { extractFromResume, getTechnologieMatch, isLoading } = useAutofillFromResume(
    candidateId as string
  );

  const getCandidateProjects = useApiFetch(
    [FetchApiIds.CANDIDATE_PROJECTS, candidateId],
    async () => candidateProfile.getAllProjects({ candidateId })
  );

  const formattedProjects = useMemo(() => {
    const projectsFallback = getCandidateProjects?.data?.projects ?? [];
    return projectsFallback.map((project) => {
      return {
        id: project?.id,
        title: project?.title,
        link: project?.link,
        description: project?.description,
        year: project?.year,
        skills: (project?.skills ?? []).map((skill) => skill?.skill?.id),
        isEditing: false,
      };
    });
  }, [getCandidateProjects.data]);

  useEffect(() => {
    if (formattedProjects.length) {
      setProjects(formattedProjects);
    }
  }, [formattedProjects]);

  const createNewProject = useApiSubmit<any, NewProjectType>(candidateProfile.createNewProject);
  const updateProject = useApiSubmit<any, NewProjectType>(candidateProfile.updateProject);
  const deleteProjectMutation = useApiSubmit<any, Pick<NewProjectType, 'id' | 'candidateId'>>(
    candidateProfile.deleteProject
  );

  const allTechStacksResponse = useApiFetch<TechStackType[], void>(
    FetchApiIds.TECH_STACKS,
    candidatesServices.getAllTechsStacks
  );

  const formattedSkills = useMemo(() => {
    return (allTechStacksResponse?.data ?? []).map((skill) => ({
      value: skill.id,
      label: skill.name,
    }));
  }, [allTechStacksResponse.data]);

  useEffect(() => {
    if (!projects.length) {
      setProjects([defaultCandidateProject]);
    }
  }, [projects]);

  const onAddNewProject = (): void => {
    setProjects((prev) => prev.concat(defaultCandidateProject));
  };

  const onDeleteProject = async (index: number): Promise<void> => {
    const projectIdToDelete = projects[index]?.id ?? '';
    if (projectIdToDelete) {
      await deleteProjectMutation.mutateAsync({
        id: projectIdToDelete,
        candidateId,
      });
      toast.showSuccessFeedback('The project has been deleted.');
    }
    setProjects((prev) => prev.filter((p, i) => i !== index));
    setProjectIndexToDelete('');
  };

  const onEditProject = (index: number): void => {
    setProjects((prev) =>
      prev.map((project, i) => {
        if (i !== index) {
          return project;
        }
        return {
          ...project,
          isEditing: true,
        };
      })
    );
  };

  const onSaveProject = async (values: NewProjectType, index: number): Promise<void> => {
    try {
      const isEditing = !!projects[index].id;
      const methodToCall = isEditing ? updateProject : createNewProject;
      const newProject = await methodToCall.mutateAsync({ ...values, candidateId });

      setProjects((prev) => {
        return prev.map((project, i) => {
          if (i !== index) {
            return project;
          }
          return {
            ...project,
            ...values,
            id: newProject.id,
            isEditing: false,
          };
        });
      });
      toast.showSuccessFeedback('The project has been saved.');
    } catch {
      toast.showErrorFeedback('There was a problem to save the project.');
    }
  };

  const updateIndexToDelete = (index: number): void => {
    setProjectIndexToDelete(index.toString());
  };

  const onCancelEdition = (index: number): void => {
    const projectIdToDelete = projects[index]?.id ?? '';
    if (!projectIdToDelete) {
      setProjects((prev) => prev.filter((p, i) => i !== index));
    } else {
      setProjects((prev) => {
        return prev.map((project, i) => {
          if (i !== index) {
            return project;
          }
          return {
            ...project,
            isEditing: false,
          };
        });
      });
    }
  };

  const isCreatingOrUpdatingProject = createNewProject.isLoading || updateProject.isLoading;

  const onAutofillFromResume = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    e.stopPropagation();

    try {
      const projectsFromResume = await extractFromResume('projects');

      const projectsList =
        typeof projectsFromResume === 'string'
          ? JSON.parse(projectsFromResume)
          : projectsFromResume;

      if (!Array.isArray(projectsList)) {
        throw new Error('No projects found in resume.');
      }

      const formattedProjectsList = projectsList.map((currentProject) => ({
        ...currentProject,
        skills: currentProject.techs
          .map((tech: string) => getTechnologieMatch(tech))
          .filter(Boolean),
      }));

      await Promise.all(
        formattedProjectsList.map(async (currentProject) => {
          const { ...projectData } = currentProject;
          delete projectData.techs;

          const newEducation = await createNewProject.mutateAsync({
            ...projectData,
            candidateId,
          });
          return newEducation;
        })
      );

      setProjects((prev) => [...prev, ...formattedProjectsList]);

      toast.showSuccessFeedback('Projects imported successfully.');
    } catch (error) {
      toast.showErrorFeedback(
        'No projects found in resume. Please check if the resume is a valid PDF file.'
      );
    }
  };

  return (
    <>
      <SectionBox
        id="projects"
        title="Projects"
        leftElement={
          isLoading ? (
            <Spinner size="xs" />
          ) : (
            <TransparentButton style={{ height: '24px' }} isBlueButton onClick={onAddNewProject}>
              + Add project
            </TransparentButton>
          )
        }
        rightElement={
          <Menu
            icon={MoreHorizIcon}
            menuId="autofill-projects-from-resume-button"
            menuItems={[
              {
                label: 'Autofill from Resume',
                menuItemIcon: AutoFixHigh,
                callback: onAutofillFromResume,
              },
            ]}
          />
        }
      >
        <>
          {projects.map((project, index) => {
            return (
              <CandidateProjectsComponent
                {...project}
                key={project.id ?? index.toString()}
                isSubmitting={isCreatingOrUpdatingProject}
                skillsOptions={formattedSkills}
                onDelete={() => updateIndexToDelete(index)}
                onEditInfo={() => onEditProject(index)}
                onSaveProject={(values) => onSaveProject(values, index)}
                onCancelProjectEdition={() => onCancelEdition(index)}
              />
            );
          })}
          <TransparentButton style={{ height: '24px' }} isBlueButton onClick={onAddNewProject}>
            + Add project
          </TransparentButton>
        </>
      </SectionBox>
      <DialogBox
        isSubmitting={deleteProjectMutation.isLoading}
        isOpen={!!projectIndexToDelete}
        onCancelHandler={() => setProjectIndexToDelete('')}
        onConfirmHandler={() => onDeleteProject(Number(projectIndexToDelete))}
      />
    </>
  );
}
